// Backdrop layer that crossfades color segments across the global scroll timeline.

import React from 'react';
import { motion, useTransform, type MotionValue } from 'framer-motion';
import { getColorStop } from '@/styles/playerColors';
import {
  sampleStops,
  toLocalClipVh,
} from '../../../utilities/scrollTimelineMath';
import type { ScrollBackdropSegment } from '../../../types/homeScroll';
import type { MotionStop } from '../../../types/homeScroll';

type ScrollBackdropProps = {
  scrollVh: MotionValue<number>;
  segments: ScrollBackdropSegment[];
  /** Optional layer rendered above the always-on base fill but beneath the
   *  segment color washes. Use for decorative content that should be visible
   *  only when no segment is fully opaque (i.e. start/end of the timeline). */
  behindSegments?: React.ReactNode;
};

type ScrollBackdropSegmentLayerProps = {
  scrollVh: MotionValue<number>;
  segment: ScrollBackdropSegment;
  fill: string;
};

const BACKDROP_OPACITY_STOPS: MotionStop[] = [
  [0, 0],
  [20, 1],
  [95, 1],
  [100, 0],
];

const ScrollBackdropSegmentLayer: React.FC<ScrollBackdropSegmentLayerProps> = ({
  scrollVh,
  segment,
  fill,
}) => {
  const segmentOpacity = useTransform(scrollVh, (value) =>
    sampleStops(
      BACKDROP_OPACITY_STOPS,
      toLocalClipVh(value, segment.startVh, segment.durationVh)
    )
  );

  return (
    <motion.div
      className="scroll-shell__fill"
      style={{
        background: fill,
        opacity: segmentOpacity,
      }}
    />
  );
};

const ScrollBackdrop: React.FC<ScrollBackdropProps> = ({
  scrollVh,
  segments,
  behindSegments,
}) => {
  return (
    <>
      <div
        className="scroll-shell__fill"
        style={{ background: 'var(--color-background)', opacity: 1 }}
      />
      {behindSegments}
      {segments.map((segment) => (
        <ScrollBackdropSegmentLayer
          key={segment.id}
          scrollVh={scrollVh}
          segment={segment}
          fill={getColorStop(segment.colorFamily, '200')}
        />
      ))}
    </>
  );
};

export default ScrollBackdrop;
